import { FC, ReactNode, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { Box, rem, ToastContainerSyle, useBreakpointValue } from '@bonusly/design-system';

import { UserMobileNavbar } from '../../modules/user-navbar/components/user-mobile-navbar';
import { UserNavbar } from '../../modules/user-navbar/components/user-navbar';
import { GLOBAL_MOBILE_BOTTOM_NAV_HEIGHT, GlobalMobileBottomNav } from '../action-bar/global-mobile-bottom-nav';
import { BizySidePanel } from '../bizy-side-panel';
import { useBizyLayout } from '../bizy-side-panel/hooks/use-bizy-layout';
import { BugRecorderBanner, useBugMode } from '../developer/bug-recorder-banner';
import { SpoofingWarning } from '../developer/spoofing-warning';
import { Reveal } from '../display/reveal';
import { useSeasonalTheme } from '../seasonal';
import { useSpoofing } from './hooks/useSpoofing';

export interface AppLayoutProps {
	children: ReactNode;
}

export const AppLayout: FC<AppLayoutProps> = ({ children }) => {
	const { data } = useSpoofing();
	const { isSeasonalThemeEnabled } = useSeasonalTheme();
	const isBugMode = useBugMode();
	const [isExpanded, setIsExpanded] = useState(true);
	const [isSidebarAnimating, setIsSidebarAnimating] = useState(false);
	const sidebarTimerRef = useRef<ReturnType<typeof setTimeout>>();
	useEffect(() => () => clearTimeout(sidebarTimerRef.current), []);

	const toggleSidebar = () => {
		setIsSidebarAnimating(true);
		setIsExpanded((prev) => !prev);
		clearTimeout(sidebarTimerRef.current);
		sidebarTimerRef.current = setTimeout(() => setIsSidebarAnimating(false), 200);
	};

	const isMobile = useBreakpointValue({ base: true, sm: false }) ?? true;

	const location = useLocation();
	const isMeetingDetailRoute = /^\/(meet\/(with\/)?[^/]+|team\/[^/]+\/meet)/.test(location.pathname);

	const bizy = useBizyLayout({ isMobile, isExpanded, setIsExpanded });

	const isSpoofing = data?.is_spoofing && !data?.is_demoing;
	const showBugBanner = isBugMode && !isSpoofing;
	const spoofingBannerHeight = rem(60);
	const mobileNavbarHeight = rem(56);
	const mobileBottomNavHeight = rem(GLOBAL_MOBILE_BOTTOM_NAV_HEIGHT);
	const hideMobileNav = isMobile && isMeetingDetailRoute;
	const hideMobileHeader = hideMobileNav;

	const sidebarPx = isExpanded ? 260 : 60;
	const currentSidebarWidth = rem(sidebarPx);
	const contentDesktopMargin = bizy.isDesktop ? (bizy.isPanelOpen ? rem(sidebarPx + bizy.panelWidth) : currentSidebarWidth) : currentSidebarWidth;

	const EASING = 'cubic-bezier(.4,0,.2,1)';
	const layoutDuration = isSidebarAnimating ? '0.2s' : '0.35s';

	const getMinHeight = () => {
		if (isMobile) {
			if (hideMobileHeader) {
				return isSpoofing ? `calc(100vh - ${spoofingBannerHeight})` : '100vh';
			}
			return isSpoofing ? `calc(100vh - ${spoofingBannerHeight} - ${mobileNavbarHeight})` : `calc(100vh - ${mobileNavbarHeight})`;
		}
		return isSpoofing ? `calc(100vh - ${spoofingBannerHeight})` : '100vh';
	};

	return (
		<Box bg={isSeasonalThemeEnabled ? 'transparent' : 'bg.surface'} minH="100vh">
			<Reveal if={isSpoofing}>
				<SpoofingWarning />
			</Reveal>
			<Reveal if={showBugBanner}>
				<BugRecorderBanner />
			</Reveal>
			<ToastContainerSyle style={{ 'padding-top': '0px' }} />

			{/* TOP NAVBAR - Mobile only, hidden on meeting detail pages */}
			<Reveal if={isMobile && !hideMobileHeader}>
				<UserMobileNavbar isSpoofing={isSpoofing} />
			</Reveal>

			<Box display="flex" minH={getMinHeight()}>
				{/* USER NAVIGATION - Desktop only */}
				<Reveal if={!isMobile}>
					<Box
						position="fixed"
						left="0"
						top={isSpoofing ? spoofingBannerHeight : '0'}
						height={isSpoofing ? `calc(100vh - ${spoofingBannerHeight})` : '100vh'}
						width={currentSidebarWidth}
						bg={isSeasonalThemeEnabled ? 'transparent' : 'bg.canvas'}
						zIndex={11}
						transition="width 0.2s cubic-bezier(.4,0,.2,1)"
						overflow="hidden"
					>
						<UserNavbar isExpanded={isExpanded} toggleSidebar={toggleSidebar} showBizyToggle={bizy.isDesktop} isBizyPanelOpen={bizy.isPanelOpen} />
					</Box>
				</Reveal>

				<Reveal if={bizy.isDesktop}>
					<Box
						position="fixed"
						left={bizy.isPanelOpen ? rem(sidebarPx) : rem(sidebarPx - bizy.panelWidth)}
						top={isSpoofing ? spoofingBannerHeight : '0'}
						height={isSpoofing ? `calc(100vh - ${spoofingBannerHeight})` : '100vh'}
						width={rem(bizy.panelWidth)}
						borderRight={bizy.isPanelOpen ? '1px solid' : 'none'}
						borderColor="border.soft"
						zIndex="docked"
						opacity={bizy.isPanelOpen ? 1 : 0}
						pointerEvents={bizy.isPanelOpen ? 'auto' : 'none'}
						transition={bizy.isResizing ? 'none' : `left ${layoutDuration} ${EASING}, width ${layoutDuration} ${EASING}, opacity 0.2s ${EASING}`}
					>
						<BizySidePanel gradientTheme="cool-subtle" panelLeftEdge={sidebarPx} />
					</Box>
				</Reveal>

				{/* CONTENTS */}
				<Box
					as="main"
					ml={{ base: 0, sm: contentDesktopMargin }}
					pb={{ base: hideMobileNav ? 0 : mobileBottomNavHeight, sm: 0 }}
					flex="1"
					minW="0"
					transition={bizy.isResizing ? 'none' : `margin-left ${layoutDuration} ${EASING}`}
				>
					{children}
				</Box>
			</Box>

			{/* BOTTOM NAVIGATION - Mobile only, hidden on meeting detail pages */}
			<Reveal if={isMobile && !hideMobileNav}>
				<GlobalMobileBottomNav />
			</Reveal>
		</Box>
	);
};
